import { DeviceType, type DeviceTypeRepository } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { DeviceTypeTopology, ParseDeviceTypeMapper } from './parse-device-type.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';

export class ParseDeviceTypeRepository extends ParseBaseRepository implements DeviceTypeRepository {
  private static readonly COUNT_DEVICE_TYPES_PAGINATION_LIMIT = 25;

  private readonly deviceTypeMapper = new ParseDeviceTypeMapper(this.parse);

  public async getById(id: string): Promise<DeviceType> {
    const deviceType = await new this.parse.Query(DeviceTypeTopology.TABLE).get(id, this.options);
    return this.deviceTypeMapper.toDomain(deviceType);
  }

  public async delete(id: string): Promise<void> {
    const parseDeviceType = this.subclasses.getSubclass(DeviceTypeTopology.TABLE).createWithoutData(id);
    await parseDeviceType.save(
      {
        [SoftDeletableTopology.DELETED_AT]: new Date(),
      },
      this.options
    );
  }

  public async getAll(searchTerm?: string, limit?: number, lastItemName?: string): Promise<DeviceType[]> {
    const query = this.buildDeviceTypeQuery(searchTerm);

    query.ascending(DeviceTypeTopology.NAME);

    if (limit) {
      query.limit(limit);
    }

    if (lastItemName) {
      query.greaterThan(DeviceTypeTopology.NAME, lastItemName);
    }

    const parseDeviceTypes = await query.find(this.options);
    return parseDeviceTypes.map((pdt) => this.deviceTypeMapper.toDomain(pdt));
  }

  public async count(searchTerm?: string): Promise<number> {
    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = this.buildDeviceTypeQuery(searchTerm);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseDeviceTypeRepository.COUNT_DEVICE_TYPES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseDeviceTypeRepository.COUNT_DEVICE_TYPES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  private buildDeviceTypeQuery(searchTerm?: string): Parse.Query {
    const query = new this.parse.Query(DeviceTypeTopology.TABLE);

    if (searchTerm && searchTerm.trim() !== '') {
      query.matches(DeviceTypeTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    return query;
  }
}
